// The electrical schematic on a sheet (docs/claude/elecSchematic.md): the pure
// model (elecSchematic.ts), layout (elecLayout.ts) and edits (elecEdits.ts)
// wired to the app — the drawing's language, the cable lengths, a canvas to
// measure text on, the sheet whose view shows a box — in ONE owner of the
// schematic in use (`elecSchematicNow`), and drawn in paper mm through a
// PaperView, on screen and in print alike.

import type {
  Drawing,
  ElecView,
  Sheet,
  Vec2,
} from '../shared/types';
import {
  buildElecSchematic,
  type ElecModel,
  type ElecTr,
} from './elecSchematic';
import {
  applyElecEdit,
  type ElecEdit,
  type ElecEditContext,
  elecEditRefusal,
  type ElecRefusal,
  elecSettleShown,
} from './elecEdits';
import {
  ELEC_AUTO_MARK,
  ELEC_BADGE_GAP,
  ELEC_BADGE_SCALE,
  ELEC_BOX_PAD,
  ELEC_BRIDGE_R,
  ELEC_DOT_R,
  ELEC_LINE_MM,
  ELEC_SWATCH,
  ELEC_TEXT_MM,
  elecAutoMark,
  elecBoxIsAuto,
  type ElecLayout,
  type ElecLayoutOptions,
  type ElecLayoutWire,
  type ElecPick,
  elecPickShape,
  elecViewBoxes,
  layoutElecSchematic,
  type Rect,
  translateElecLayout,
} from './elecLayout';
import { connectionLength } from './cableLength';
import {
  dashArray,
  strokeWithHumps,
} from './lineHops';
import { trIn } from './i18n';
import {
  paperToScreen,
  type PaperView,
} from './sheets';

/** The schematic's typeface: drawn, measured and typed in. */
export const ELEC_FONT = 'Arial, Helvetica, sans-serif';

// ---- Measuring -------------------------------------------------------------------------

let measureCtx: CanvasRenderingContext2D | null = null;
const widths = new Map<string, number>();
/** A text's width in paper mm at the schematic's 7 pt, measured on a canvas. */
export function elecTextWidth(s: string): number {
  const hit = widths.get(s);
  if (hit != null) return hit;
  measureCtx ??= document.createElement('canvas').getContext('2d');
  let w = s.length * ELEC_TEXT_MM * 0.5;
  if (measureCtx) { measureCtx.font = `100px ${ELEC_FONT}`; w = (measureCtx.measureText(s).width / 100) * ELEC_TEXT_MM; }
  widths.set(s, w);
  return w;
}

// ---- The schematic in use: the model and the views' layouts ----------------------------

/** Text drawn IN the drawing follows the drawing's language. */
export const elecTr = (d: Drawing): ElecTr => (s) => trIn(s, d.language ?? 'it');

/** Every view of the drawing, in print order: its sheets', each sheet's in turn. */
const allViews = (d: Drawing): ElecView[] => (d.sheets ?? []).flatMap((sh) => sh.elecViews ?? []);

/** Whether a view has chosen its columns (either list); one with neither shows
 *  every column. */
const chosen = (v: ElecView): boolean => !!(v.columns || v.leaves);

/** The columns a view shows, in the schematic's order. A view with no lists
 *  shows every column. One with lists shows those it shows by choice
 *  (`columns`), never those it leaves out (`leaves`), and a column no view with
 *  lists has chosen either way (a room added since) all the same: in the view
 *  with lists showing the column before it in the schematic's order, else in the
 *  first such view (print order) — so it is always in a view, one view alone
 *  included (the user's decision; it was once in none after a Split). */
export function elecViewColumnsIn(model: ElecModel, d: Drawing, view: ElecView): string[] {
  if (!chosen(view)) return model.columns.map((c) => c.id);
  const listed = allViews(d).filter(chosen);
  const views = listed.includes(view) ? listed : [view];
  const out: string[] = [];
  let prev: ElecView | null = null;
  for (const { id } of model.columns) {
    if (views.some((v) => v.columns?.includes(id) || v.leaves?.includes(id))) {
      prev = views.find((v) => v.columns?.includes(id)) ?? prev;
      if (view.columns?.includes(id)) out.push(id);
      continue;
    }
    prev ??= views[0];
    if (prev === view) out.push(id);
  }
  return out;
}

/** Each column a view with lists shows only by the rule above written into its
 *  `columns`, where it is: run in the step of a change to views' lists (a tick,
 *  Split), so what that change decides does not take a column from the view
 *  showing it — a room added since, ticked in another view, is then in both. */
export function elecSettleViewColumns(model: ElecModel, d: Drawing): void {
  const listed = allViews(d).filter(chosen);
  const pins = listed.map((v) => elecViewColumnsIn(model, d, v).filter((id) => !v.columns?.includes(id)));
  listed.forEach((v, i) => { if (pins[i].length) v.columns = [...(v.columns ?? []), ...pins[i]]; });
}

/** The lists Split writes on its views, group i on view i: each shows its group
 *  and leaves out every other column the schematic shows; what the view left
 *  out that the schematic has no column for now (hidden, or a room with no box)
 *  stays left out of each, and what it showed of those stays in the first. */
export function elecSplitLists(model: ElecModel, view: ElecView, groups: readonly string[][]): Array<{ columns: string[]; leaves: string[] }> {
  const shown = model.columns.map((c) => c.id);
  const elsewhere = (ids?: string[]): string[] => (ids ?? []).filter((id) => !shown.includes(id));
  return groups.map((g, i) => ({
    columns: [...g, ...(i ? [] : elsewhere(view.columns))],
    leaves: [...shown.filter((id) => !g.includes(id)), ...elsewhere(view.leaves)],
  }));
}

/** Views about to go — deleted, cut, their sheet deleted — hand back the columns
 *  only they show (and those they show by choice that the schematic hides now):
 *  taken out of what the other views leave out, so no view has chosen them and
 *  they join the view showing the column before each, as a room added later
 *  does (the user's decision). Run in the step of the delete, before it. What a
 *  view with no lists shows does not keep a column: it shows every one. */
export function elecHandBackColumns(model: ElecModel, d: Drawing, gone: readonly ElecView[]): void {
  const stay = allViews(d).filter((v) => !gone.includes(v) && chosen(v));
  const kept = new Set(stay.flatMap((v) => [...elecViewColumnsIn(model, d, v), ...(v.columns ?? [])]));
  const back = new Set(gone.flatMap((v) => [...elecViewColumnsIn(model, d, v), ...(v.columns ?? [])]).filter((id) => !kept.has(id)));
  for (const v of stay) if (v.leaves?.some((id) => back.has(id))) v.leaves = v.leaves.filter((id) => !back.has(id));
}

/** A drawing opened: a view saved when a view listed only the columns it shows
 *  leaves out every other column known then (the hidden too), so it looks as it
 *  did; an empty list still marks it settled. The model is built only for one. */
export function settleElecViewLeaves(d: Drawing, model: () => ElecModel): void {
  const old = allViews(d).filter((v) => v.columns && !v.leaves);
  if (!old.length) return;
  const m = model(), known = [...m.columns, ...m.hiddenColumns].map((c) => c.id);
  for (const v of old) v.leaves = known.filter((id) => !v.columns!.includes(id));
}

/** Where each box is shown, by the views of every sheet: the sheet's number (its
 *  place in the print order) for "→ sheet 2, wire E14". */
function shownOn(model: ElecModel, d: Drawing, except: ElecView): Map<string, string> {
  const out = new Map<string, string>();
  (d.sheets ?? []).forEach((sh, i) => {
    for (const v of sh.elecViews ?? []) {
      if (v === except) continue;
      for (const k of elecViewBoxes(model, { columns: elecViewColumnsIn(model, d, v) })) if (!out.has(k)) out.set(k, String(i + 1));
    }
  });
  return out;
}

/** A view at the page's origin, its table and legend where they stand from it. */
function atOrigin(view: ElecView): ElecView {
  const rel = (b: ElecView['table']): ElecView['table'] => b && { ...b, x: b.x - view.x, y: b.y - view.y };
  return { ...view, x: 0, y: 0, ...(view.table ? { table: rel(view.table) } : {}), ...(view.legend ? { legend: rel(view.legend) } : {}) };
}

/** Where the schematic in use comes from: the app's drawing and revision, and
 *  how its texts are measured. */
export interface ElecSource {
  drawing(): Drawing;
  /** The app's drawing revision: it changes with every recorded step, an undo. */
  revision(): number;
  /** A text's width (mm) at the schematic's size; absent = measured on a canvas. */
  textWidth?: (s: string) => number;
}

/** The schematic in use, the one owner of its model and of its views' layouts:
 *  each built again only when what it was built from changed — the drawing, its
 *  revision, its language, its stored edits, any edit made through `edit` (so a
 *  read between the undo step and the edit never keeps the schematic as it was),
 *  and for a layout its view and the other views' columns. Where a view stands
 *  does not matter: its layout is made at the page's origin and moved there, so a
 *  view dragged is never laid out again. */
export interface ElecSchematicNow {
  model(): ElecModel;
  /** A view laid out where it stands. */
  layout(view: ElecView): ElecLayout;
  /** Every view on a sheet with its layout: what the app draws and picks. */
  sheet(sh: Sheet): Array<{ view: ElecView; layout: ElecLayout }>;
  /** What a view's layout is given (Split lays out with it too). */
  options(view: ElecView): ElecLayoutOptions;
  /** The columns a view shows (`elecViewColumnsIn`): its panel's ticks, Split. */
  columns(view: ElecView): string[];
  /** Why an edit cannot be made, else null. */
  refusal(edit: ElecEdit): ElecRefusal | null;
  /** Make an edit on the schematic as it is now; returns the key of what it added. */
  edit(edit: ElecEdit): string | null;
}

export function elecSchematicNow(src: ElecSource): ElecSchematicNow {
  const textWidth = src.textWidth ?? elecTextWidth;
  let edits = 0;
  let built: { drawing: Drawing; key: string; model: ElecModel } | null = null;
  const placed = new Map<ElecView, { model: ElecModel; key: string; base: ElecLayout; x: number; y: number; layout: ElecLayout }>();
  const model = (): ElecModel => {
    const d = src.drawing();
    const key = `${src.revision()}|${edits}|${d.language ?? 'it'}|${JSON.stringify(d.elecSchematic ?? null)}`;
    if (built?.drawing === d && built.key === key) return built.model;
    built = { drawing: d, key, model: buildElecSchematic(d, { tr: elecTr(d), lengthOf: (c) => connectionLength(d, c) }) };
    return built.model;
  };
  const options = (m: ElecModel, view: ElecView): ElecLayoutOptions => {
    const d = src.drawing();
    let where: Map<string, string> | null = null;
    return { tr: elecTr(d), textWidth, whereIs: (k) => (where ??= shownOn(m, d, view)).get(k) };
  };
  const layout = (view: ElecView): ElecLayout => {
    const m = model(), d = src.drawing(), rel = atOrigin(view);
    const key = `${JSON.stringify(rel)}|${JSON.stringify((d.sheets ?? []).map((s) => (s.elecViews ?? []).map((v) => v.columns ?? null)))}`;
    let hit = placed.get(view);
    if (!hit || hit.model !== m || hit.key !== key) {
      if (placed.size > 64) placed.clear();
      const base = layoutElecSchematic(m, { ...rel, columns: elecViewColumnsIn(m, d, view) }, options(m, view));
      hit = { model: m, key, base, x: 0, y: 0, layout: base };
      placed.set(view, hit);
    }
    if (hit.x !== view.x || hit.y !== view.y) Object.assign(hit, { x: view.x, y: view.y, layout: translateElecLayout(hit.base, view.x, view.y) });
    return hit.layout;
  };
  // Every view's client row as it stands it by itself, for a client-row order.
  const clientAuto = (): string[][] => (src.drawing().sheets ?? []).flatMap((s) => s.elecViews ?? []).map((v) => layout(v).clientAuto);
  const ctx = (): ElecEditContext => ({ drawing: src.drawing(), model: model(), tr: elecTr(src.drawing()), clientAuto });
  return {
    model,
    layout,
    sheet: (sh) => (sh.elecViews ?? []).map((view) => ({ view, layout: layout(view) })),
    options: (view) => options(model(), view),
    columns: (view) => elecViewColumnsIn(model(), src.drawing(), view),
    refusal: (edit) => elecEditRefusal(ctx(), edit),
    edit: (edit) => {
      // Shown again: the client row's boxes it brings back are placed once
      // every view draws them (elecSettleShown).
      const shows = (edit.kind === 'boxHidden' && !edit.hidden) || (edit.kind === 'column' && edit.hidden === false);
      const was = shows ? new Set(model().boxes.filter((b) => b.cell === 'client').map((b) => b.key)) : null;
      const made = applyElecEdit(ctx(), edit);
      edits++;
      if (was) elecSettleShown(ctx(), was);
      return made;
    },
  };
}

/** Every view on a sheet laid out afresh — what a web case bundles into the page
 *  to know where a box is drawn. */
export function elecSheetLayouts(d: Drawing, sh: Sheet): Array<{ view: ElecView; layout: ElecLayout }> {
  return elecSchematicNow({ drawing: () => d, revision: () => 0 }).sheet(sh);
}

// ---- Drawing ---------------------------------------------------------------------------------

const SEL = '#4aa3ff';
const GRID_INK = '#9a9a9a';
const HEAD_FILL = '#dcd8d1';
const BOX_FILL = '#ececec';
const BAR_FILL = '#e2e2e2';
const MANAGER_FILL = '#cfd0f5';
const CLIENT_FILL = '#f8b096';

/** What the app shows on top of a view: its selection, a box being dragged. */
export interface ElecDrawState {
  selected?: boolean;
  /** The part of the selected view that is picked. */
  sub?: ElecPick | null;
  /** A box dragged by hand: its offset (paper mm) and the cell it would land in. */
  drag?: { key: string; dx: number; dy: number; target?: Rect } | null;
  /** The first box of a wire being drawn. */
  wireFrom?: string | null;
  /** A wire being drawn, or an end being dragged: a dashed line from `from` to
   *  `to`, the box it would land on outlined, the wire it moves faded. */
  rubber?: { from: Vec2; to: Vec2; color: string; target?: string | null; dim?: string } | null;
  /** The triangle in the corner of each box the schematic made itself (on
   *  screen only), at least `autoMarkPx` pixels. */
  autoMarks?: boolean;
  autoMarkPx?: number;
}

/** The auto mark's grey. */
const AUTO_FILL = '#8a96a6';
/** On screen, an auto mark is never smaller than this (px). */
export const ELEC_AUTO_MARK_PX = 7;

/** Draw a placed view: grid, headers, cells, boxes, wires and their badges and
 *  off-sheet ends, the table and the legend. */
export function drawElecView(ctx: CanvasRenderingContext2D, pv: PaperView, layout: ElecLayout, ink: string, ui: ElecDrawState = {}): void {
  const S = (x: number, y: number): Vec2 => paperToScreen(pv, x, y);
  const k = pv.scale;
  const lw = (mm: number): number => Math.max(0.5, mm * k);
  const fontPx = ELEC_TEXT_MM * k;
  const rect = (r: Rect, fill: string | null, stroke: string | null, width = 0.2): void => {
    const a = S(r.x, r.y);
    if (fill) { ctx.fillStyle = fill; ctx.fillRect(a.x, a.y, r.w * k, r.h * k); }
    if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = lw(width); ctx.strokeRect(a.x, a.y, r.w * k, r.h * k); }
  };
  const text = (s: string, x: number, y: number, o: { rot?: boolean; color?: string; size?: number; align?: CanvasTextAlign; bold?: boolean } = {}): void => {
    if (!s) return;
    const a = S(x, y);
    ctx.save();
    ctx.translate(a.x, a.y);
    if (o.rot) ctx.rotate(-Math.PI / 2);
    ctx.font = `${o.bold ? 'bold ' : ''}${Math.max(0.5, (o.size ?? 1) * fontPx)}px ${ELEC_FONT}`;
    ctx.fillStyle = o.color ?? ink;
    ctx.textAlign = o.align ?? 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(s, 0, 0);
    ctx.restore();
  };
  const line = (pts: Vec2[], color: string, width: number): void => {
    ctx.strokeStyle = color; ctx.lineWidth = lw(width);
    ctx.beginPath();
    pts.forEach((p, i) => { const s = S(p.x, p.y); if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y); });
    ctx.stroke();
  };
  const ascent = ELEC_TEXT_MM * 0.78;
  // A wire run by run, humped over the wires it crosses (`bridges`), in its own
  // width and dash — the dash a third of a sheet line's, for its finer scale.
  const wireLine = (w: ElecLayoutWire, color: string, width: number, dashed: boolean): void => {
    ctx.strokeStyle = color; ctx.lineWidth = lw(width);
    ctx.lineCap = w.style === 'dotted' ? 'round' : 'square';
    ctx.setLineDash(dashed ? dashArray(w.style, k / 3) : []);
    for (let i = 0; i < w.points.length - 1; i++) {
      const hops = w.bridges.filter((b) => b.seg === i).map((b) => S(b.at.x, b.at.y));
      // A level run is stroked right to left: its humps then bulge up, where the
      // layout keeps the badges clear of them.
      let a = w.points[i], b = w.points[i + 1];
      if (Math.abs(a.y - b.y) < 1e-9 && a.x < b.x) [a, b] = [b, a];
      strokeWithHumps(ctx, S(a.x, a.y), S(b.x, b.y), hops, ELEC_BRIDGE_R * k);
    }
    ctx.setLineDash([]);
    ctx.lineCap = 'butt';
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.lineJoin = 'miter';
  // Cells, then the grid's lines over them.
  for (const c of layout.cells) rect(c.rect, c.color ?? '#ffffff', null);
  for (const r of layout.rows) {
    rect(r.header.rect, HEAD_FILL, GRID_INK);
    const h = r.header.rect;
    text(r.header.text, h.x + h.w / 2 + ascent / 2, h.y + h.h / 2, { rot: true, align: 'center' });
    line([{ x: r.rect.x, y: r.rect.y }, { x: r.rect.x + r.rect.w, y: r.rect.y }], GRID_INK, 0.2);
  }
  for (const b of layout.borders) line([{ x: b.x, y: b.y0 }, { x: b.x, y: b.y1 }], GRID_INK, 0.2);
  for (const c of layout.columns) {
    rect(c.header, c.color, GRID_INK);
    text(c.text, c.header.x + ELEC_BOX_PAD * 2, c.header.y + c.header.h / 2 + ascent / 2, { bold: true });
  }
  for (const c of layout.cells) if (c.label && !c.labelGround) text(c.label, c.labelRect?.x ?? c.rect.x + ELEC_BOX_PAD * 2, (c.labelRect?.y ?? c.rect.y + ELEC_BOX_PAD) + ascent);
  rect(layout.grid, null, ink, 0.3);

  // Wires under the boxes, their dots and badges over them; the picked one glows.
  const shape = ui.selected && ui.sub ? elecPickShape(layout, ui.sub) : null;
  const subWire = shape && 'wire' in shape ? shape.wire : null;
  for (const w of layout.wires) {
    if (w.key === ui.rubber?.dim) ctx.globalAlpha = 0.3;
    if (w.key === subWire) wireLine(w, 'rgba(74,163,255,0.45)', w.thickness + 0.9, false);
    wireLine(w, w.color, w.thickness, true);
    ctx.globalAlpha = 1;
  }
  // A plant room's name with no place clear of the wires: over them, on white.
  for (const c of layout.cells) {
    if (!c.label || !c.labelGround || !c.labelRect) continue;
    const g = c.labelRect;
    rect({ x: g.x - ELEC_BADGE_GAP, y: g.y - ELEC_BADGE_GAP, w: g.w + 2 * ELEC_BADGE_GAP, h: g.h + 2 * ELEC_BADGE_GAP }, '#ffffff', null);
    text(c.label, g.x, g.y + ascent);
  }

  for (const b of layout.boxes) {
    const fill = b.box.row === 'client' ? CLIENT_FILL : b.box.manager ? MANAGER_FILL : b.line % 2 ? BAR_FILL : BOX_FILL;
    const r = b.rect;
    const dragged = ui.drag?.key === b.key;
    rect(r, fill, dragged ? GRID_INK : ink, 0.25);
    if (dragged) continue;
    // Its lines as read (left to right, down), in a frame turned by its angle
    // about its middle: 270 runs them up the box, 90 down it, 180 upside down.
    const n = b.lines.length, turned = b.angle === 90 || b.angle === 270;
    const W = turned ? r.h : r.w, H = turned ? r.w : r.h;
    const c = S(r.x + r.w / 2, r.y + r.h / 2);
    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.rotate((b.angle * Math.PI) / 180);
    ctx.font = `${Math.max(0.5, fontPx)}px ${ELEC_FONT}`;
    ctx.fillStyle = ink;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const y0 = -H / 2 + Math.max(ELEC_BOX_PAD, (H - n * ELEC_LINE_MM) / 2);
    b.lines.forEach((s, i) => { if (s) ctx.fillText(s, (-W / 2 + ELEC_BOX_PAD) * k, (y0 + ascent + i * ELEC_LINE_MM) * k); });
    ctx.restore();
    if (ui.autoMarks && elecBoxIsAuto(b)) {
      const tri = elecAutoMark(r, Math.max(ELEC_AUTO_MARK, (ui.autoMarkPx ?? ELEC_AUTO_MARK_PX) / k)).map((p) => S(p.x, p.y));
      ctx.fillStyle = AUTO_FILL;
      ctx.beginPath(); ctx.moveTo(tri[0].x, tri[0].y); ctx.lineTo(tri[1].x, tri[1].y); ctx.lineTo(tri[2].x, tri[2].y); ctx.closePath(); ctx.fill();
    }
    if (b.key === ui.wireFrom) rect(r, null, SEL, 0.6);
  }
  for (const w of layout.wires) {
    ctx.fillStyle = w.color;
    for (const p of w.dots) { const s = S(p.x, p.y); ctx.beginPath(); ctx.arc(s.x, s.y, Math.max(1, ELEC_DOT_R * k), 0, Math.PI * 2); ctx.fill(); }
    if (w.badge) {
      // The number along the wire, on a white ground (decision 17), where the
      // layout found it room.
      const size = ELEC_BADGE_SCALE, r = w.badge.rect, cx = r.x + r.w / 2, cy = r.y + r.h / 2;
      rect(r, '#ffffff', w.color, 0.15);
      if (w.badge.vertical) text(w.badge.text, cx + (ascent * size) / 2, cy, { rot: true, align: 'center', color: w.color, size });
      else text(w.badge.text, cx, cy + (ascent * size) / 2, { align: 'center', color: w.color, size });
    }
    if (w.offSheet) {
      // Its note, where the layout put it: outside the grid, at the wire's height.
      const o = w.offSheet, r = o.rect;
      text(o.text, o.side === 'left' ? r.x + r.w : r.x, r.y + r.h, { color: w.color, size: ELEC_BADGE_SCALE, align: o.side === 'left' ? 'right' : 'left' });
    }
  }

  // The table and the legend.
  const t = layout.table;
  if (t) {
    rect(t.rect, '#ffffff', ink, 0.25);
    const rows = [t.header, ...t.rows.map((r) => r.cells)];
    rows.forEach((cells, i) => {
      const y = t.rect.y + i * t.rowH;
      if (i) line([{ x: t.rect.x, y }, { x: t.rect.x + t.rect.w, y }], i === 1 ? ink : '#cfcfcf', i === 1 ? 0.25 : 0.12);
      let x = t.rect.x;
      cells.forEach((c, j) => {
        const color = i && j === 0 ? (t.rows[i - 1] && layout.wires.find((w) => w.key === t.rows[i - 1].key)?.color) || ink : ink;
        text(c, x + ELEC_BOX_PAD, y + t.rowH / 2 + ascent / 2, { color });
        x += t.colW[j];
      });
    });
    let x = t.rect.x;
    for (const w of t.colW.slice(0, -1)) { x += w; line([{ x, y: t.rect.y }, { x, y: t.rect.y + t.rect.h }], '#cfcfcf', 0.12); }
  }
  const g = layout.legend;
  if (g) {
    rect(g.rect, '#ffffff', ink, 0.25);
    g.entries.forEach((e, i) => {
      const y = g.rect.y + ELEC_BOX_PAD + i * g.rowH;
      rect({ x: g.rect.x + ELEC_BOX_PAD, y: y + (g.rowH - ELEC_SWATCH * 0.6) / 2, w: ELEC_SWATCH, h: ELEC_SWATCH * 0.6 }, e.color, ink, 0.15);
      text(e.text, g.rect.x + ELEC_BOX_PAD * 2 + ELEC_SWATCH, y + g.rowH / 2 + ascent / 2);
    });
  }

  // The selection.
  if (ui.selected) {
    ctx.setLineDash([5, 4]);
    rect(layout.grid, null, SEL, 0.35);
    ctx.setLineDash([]);
    if (shape && 'rect' in shape) rect(shape.rect, shape.fill ? `rgba(74,163,255,${shape.fill})` : null, SEL, shape.fill ? 0.5 : 0.6);
    else if (shape && 'line' in shape) line(shape.line, SEL, 0.6);
  }
  // A wire on its way: at right angles, as it will be drawn, to the cursor or to
  // the box it would land on.
  const rb = ui.rubber;
  if (rb) {
    const tb = rb.target ? layout.boxes.find((x) => x.key === rb.target) : undefined;
    if (tb) rect(tb.rect, 'rgba(74,163,255,0.15)', SEL, 0.6);
    // Two ends level (boxes of one line): it dips under them, as such a wire does.
    const level = Math.abs(rb.from.y - rb.to.y) < 1e-6;
    const midY = level ? rb.from.y + 3 : (rb.from.y + rb.to.y) / 2;
    ctx.setLineDash([4, 3]);
    // Bold enough to follow when the sheet is zoomed out.
    line([rb.from, { x: rb.from.x, y: midY }, { x: rb.to.x, y: midY }, rb.to], rb.color, Math.max(0.35, 1.5 / k));
    ctx.setLineDash([]);
    ctx.fillStyle = rb.color;
    for (const p of [rb.from, rb.to]) { const s = S(p.x, p.y); ctx.beginPath(); ctx.arc(s.x, s.y, Math.max(3, ELEC_DOT_R * k), 0, Math.PI * 2); ctx.fill(); }
  }
  // A box being dragged: its ghost under the cursor, the cell it lands in.
  const d = ui.drag;
  if (d) {
    if (d.target) rect(d.target, 'rgba(74,163,255,0.12)', SEL, 0.4);
    const b = layout.boxes.find((x) => x.key === d.key);
    if (b) { ctx.globalAlpha = 0.7; rect({ ...b.rect, x: b.rect.x + d.dx, y: b.rect.y + d.dy }, BOX_FILL, SEL, 0.4); ctx.globalAlpha = 1; }
  }
  ctx.restore();
}
